import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Edit, Eye, Handshake, MoreHorizontal, Trash2, UserCheck, UserX } from "lucide-react"

interface TableAction {
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  variant?: "default" | "destructive"
  requiresConfirmation?: boolean
  confirmationTitle?: string
  confirmationDescription?: string
}

interface TableActionsProps {
  actions: TableAction[]
}

/**
 * Componente de ações para linhas de tabela
 */
export function TableActions({ actions }: TableActionsProps) {
  const [confirmAction, setConfirmAction] = useState<TableAction | null>(null)

  const handleActionClick = (action: TableAction) => {
    if (action.requiresConfirmation) {
      setConfirmAction(action)
    } else {
      action.onClick()
    }
  }

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction.onClick()
      setConfirmAction(null)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Abrir menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((action, index) => (
            <div key={index}>
              {index > 0 && action.variant === "destructive" && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() => handleActionClick(action)}
                className={
                  action.variant === "destructive" ? "text-destructive focus:text-destructive" : ""
                }
              >
                <action.icon className="mr-2 h-4 w-4" />
                {action.label}
              </DropdownMenuItem>
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.confirmationTitle || "Confirmar ação"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.confirmationDescription || "Esta ação não pode ser desfeita."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                confirmAction?.variant === "destructive"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// Ações pré-definidas para usuários
export const createUserActions = (
  user: any,
  onView: (user: any) => void,
  onEdit: (user: any) => void,
  onToggleStatus: (user: any) => void,
  onDelete: (user: any) => void,
  onTogglePartner?: (user: any) => void
): TableAction[] => {
  const actions: TableAction[] = [
    {
      label: "Ver detalhes",
      icon: Eye,
      onClick: () => onView(user),
    },
    {
      label: "Editar",
      icon: Edit,
      onClick: () => onEdit(user),
    },
    {
      label: user.is_active ? "Desativar" : "Ativar",
      icon: user.is_active ? UserX : UserCheck,
      onClick: () => onToggleStatus(user),
      requiresConfirmation: true,
      confirmationTitle: `${user.is_active ? "Desativar" : "Ativar"} usuário`,
      confirmationDescription: `Tem certeza que deseja ${user.is_active ? "desativar" : "ativar"} o usuário "${user.name}"?`,
    },
  ]

  if (onTogglePartner && user.organization_id) {
    const isPartner = !!user.organization_is_partner
    actions.push({
      label: isPartner ? "Remover marca de parceiro" : "Marcar org como parceiro",
      icon: Handshake,
      onClick: () => onTogglePartner(user),
      requiresConfirmation: true,
      confirmationTitle: isPartner ? "Remover parceiro" : "Marcar como parceiro",
      confirmationDescription: isPartner
        ? `Tem certeza que deseja remover a marca de parceiro da organização do usuário "${user.name}"? A organização voltará a respeitar os limites de assinatura.`
        : `Tem certeza que deseja marcar a organização do usuário "${user.name}" como parceira? Ela ficará isenta de qualquer expiração de trial/assinatura.`,
    })
  }

  actions.push({
    label: "Excluir",
    icon: Trash2,
    onClick: () => onDelete(user),
    variant: "destructive",
    requiresConfirmation: true,
    confirmationTitle: "Excluir usuário",
    confirmationDescription: `Tem certeza que deseja excluir o usuário "${user.name}"? Esta ação não pode ser desfeita.`,
  })

  return actions
}
